/**
 * VerdictBadge — shared verdict badge component.
 *
 * Renders icon (mandatory for colour-blind users) + i18n label.
 * Replaces local VerdictBadge / VerdictIcon+verdictColor / verdictCfg
 * across SimSessionHistoryItem, CabinReadinessReport, CabinReadinessCompare.
 *
 * Props:
 *   verdict   — 'HAZIR' | 'KOSULLU' | 'DEGIL'
 *   iconSize  — icon size in px (default 11)
 *   t         — translation function from useLanguage (required for i18n label)
 *   variant   — 'sm' (default) | 'md' — controls padding/text/border size
 *   className — additional class names (appended after base classes)
 */

import { CheckCircle2, Star, Target } from 'lucide-react';

const VERDICT_CFG = {
  HAZIR: {
    Icon: CheckCircle2,
    badgeClass: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  },
  KOSULLU: {
    Icon: Star,
    badgeClass: 'bg-amber-50 border-amber-200 text-amber-700',
  },
  DEGIL: {
    Icon: Target,
    badgeClass: 'bg-rose-50 border-rose-200 text-rose-600',
  },
};

const SIZE_VARIANT = {
  sm: 'px-2 py-0.5 text-xs border',
  md: 'px-3 py-1.5 text-sm border-2',
};

export default function VerdictBadge({ verdict, iconSize = 11, t, variant = 'sm', className = '' }) {
  const cfg = VERDICT_CFG[verdict];
  if (!cfg) return null;
  const { Icon, badgeClass } = cfg;
  const label = t ? t(`readinessReport.verdict.${verdict}.title`) : verdict;
  return (
    <span
      className={`inline-flex items-center gap-1 font-bold rounded-full ${SIZE_VARIANT[variant]} ${badgeClass} ${className}`.trim()}
      aria-label={label}
    >
      <Icon size={iconSize} aria-hidden="true" />
      {label}
    </span>
  );
}
